/**
 * Repair tickets: the second state machine in the system, and the one that reaches outside.
 *
 * Lives in the shared package for the same reason the gadget lifecycle does — the API, the web
 * app and the future mobile app must agree on what is permitted, and a partner adapter must
 * not invent its own vocabulary.
 */
export const TICKET_STATES = [
    'raised', // reported, nobody has looked at it
    'triaged', // assessed, a route decided
    'dispatched', // handed to a service centre, in transit
    'in_repair', // being worked on
    'awaiting_parts', // work stalled on a part
    'completed', // fixed and returned
    'cancelled', // abandoned; the gadget goes back the way it came
];
/**
 * Cancellation is reachable from anywhere unfinished: a repair can be called off because the
 * gadget was replaced, the quote was refused, or it turned out not to be broken. Completion is
 * not — a ticket only completes from actual repair work.
 */
export const TICKET_TRANSITIONS = {
    raised: ['triaged', 'cancelled'],
    triaged: ['dispatched', 'in_repair', 'cancelled'],
    dispatched: ['in_repair', 'cancelled'],
    in_repair: ['awaiting_parts', 'completed', 'cancelled'],
    awaiting_parts: ['in_repair', 'cancelled'],
    completed: [],
    cancelled: [],
};
export const INITIAL_TICKET_STATE = 'raised';
export const TICKET_SEVERITIES = ['low', 'medium', 'high', 'critical'];
// Equivalent mutant, for the same reason as isLifecycleState: the typeof guard narrows for the
// compiler and cannot change the result.
export const isTicketState = (v) => 
// Stryker disable next-line ConditionalExpression
typeof v === 'string' && TICKET_STATES.includes(v);
export const canTicketTransition = (from, to) => TICKET_TRANSITIONS[from].includes(to);
export const isTicketClosed = (s) => TICKET_TRANSITIONS[s].length === 0;
export class InvalidTicketTransitionError extends Error {
    from;
    to;
    constructor(from, to) {
        super(TICKET_TRANSITIONS[from].length === 0
            ? `ticket is ${from}; it cannot change again`
            : `cannot move a ticket from ${from} to ${to}; permitted: ${TICKET_TRANSITIONS[from].join(', ')}`);
        this.from = from;
        this.to = to;
        this.name = 'InvalidTicketTransitionError';
    }
}
export function assertTicketTransition(from, to) {
    if (!canTicketTransition(from, to))
        throw new InvalidTicketTransitionError(from, to);
}
export function invalidTicketPairs() {
    const out = [];
    for (const from of TICKET_STATES) {
        for (const to of TICKET_STATES)
            if (!canTicketTransition(from, to))
                out.push([from, to]);
    }
    return out;
}
/**
 * Where the gadget should be while its ticket is in a given state.
 *
 * The build spec says raising a ticket moves the gadget "into the appropriate lifecycle state".
 * This table is that word made explicit: a gadget with an open repair ticket is under repair,
 * a stalled repair puts the gadget in awaiting_parts to match, and null means the ticket state
 * implies nothing — where the gadget goes after a repair ends depends on whether anyone is
 * waiting for it, which the caller knows and this table does not.
 */
export const GADGET_STATE_FOR_TICKET = {
    raised: 'under_repair',
    triaged: 'under_repair',
    dispatched: 'under_repair',
    in_repair: 'under_repair',
    awaiting_parts: 'awaiting_parts',
    completed: null,
    cancelled: null,
};
/** Total cost of ownership so far: what it cost to buy, plus every repair since. */
export function totalCostOfOwnership(purchaseMinor, repairCostsMinor) {
    return (purchaseMinor ?? 0) + repairCostsMinor.reduce((sum, c) => sum + c, 0);
}
//# sourceMappingURL=repair.js.map